import { createReducer } from '@reduxjs/toolkit'

import type { TDataRunsDto, TJobSummaryDto } from '@homepal/api-contract'

import { DataRunsCustomerOption } from 'types/GlobalDataRuns'

import * as actions from 'redux/actions/internal/DataRuns/actions'
import * as authenticationActions from 'redux/actions/Authentication/actions'

export interface DataRunsReducerType {
  data: TDataRunsDto | null
  dataFetched: boolean
  dataFetching: boolean
  /**
   * Populated from the latest unscoped (all-customers) fetch only, so the
   * customer select keeps its full option list even while a customer scope
   * is active (a scoped fetch's `jobs` only carries that one customer).
   */
  customerOptions: DataRunsCustomerOption[]
  /**
   * The scope of the most recent request (null = all customers), kept so a
   * response for an older scope can be recognized and dropped rather than
   * overwriting the current one. See the `getSuccess` case.
   */
  requestedCustomerId: string | null
  /** Jobs with a start request in flight. */
  startingJobNames: string[]
}

// Initial state
const INITIAL_STATE: DataRunsReducerType = {
  data: null,
  dataFetched: false,
  dataFetching: false,
  customerOptions: [],
  requestedCustomerId: null,
  startingJobNames: []
}

/**
 * Derives the customer select's option list from an unscoped response's
 * job list - it is the only place every customer with jobs is guaranteed to
 * appear, sparing a second call to the customers endpoint.
 */
function buildCustomerOptions(
  jobs: TJobSummaryDto[]
): DataRunsCustomerOption[] {
  const byId = new Map<string, DataRunsCustomerOption>()

  jobs.forEach((job) => {
    if (!job.customer) {
      return
    }

    const existing = byId.get(job.customer.id) ?? {
      id: job.customer.id,
      name: job.customer.name,
      failCount: 0
    }

    const isFailing = job.status === 'fail' || job.status === 'timeout'

    byId.set(job.customer.id, {
      ...existing,
      failCount: existing.failCount + (isFailing ? 1 : 0)
    })
  })

  return Array.from(byId.values())
}

// Reducer (createReducer uses immer — cases mutate the draft directly)
export default createReducer(INITIAL_STATE, (builder) => {
  builder
    .addCase(actions.get, (state, action) => {
      state.requestedCustomerId = action.payload.customerId ?? null
      state.dataFetching = true
    })
    .addCase(actions.getSuccess, (state, action) => {
      const { payload } = action

      // The all-customers request is a great deal heavier than a scoped one
      // (every customer's jobs plus Glue workflow discovery), so the one
      // fired on mount can still land after a customer has been selected
      // and its own response has arrived. Writing it anyway would leave the
      // page showing every customer's jobs under one customer's heading -
      // and without run history, since the API only sends that in customer
      // scope. Only the response for the scope currently in effect counts.
      if ((payload.customerId ?? null) !== state.requestedCustomerId) {
        return
      }

      state.data = payload.data
      state.dataFetched = true
      state.dataFetching = false

      if (!payload.customerId) {
        state.customerOptions = buildCustomerOptions(payload.data.jobs)
      }
    })
    .addCase(actions.getFailed, (state, action) => {
      // Same guard: a superseded request failing must not clear the
      // spinner for the request that replaced it.
      if ((action.payload.customerId ?? null) !== state.requestedCustomerId) {
        return
      }

      state.dataFetching = false
    })
    .addCase(actions.startJob, (state, action) => {
      state.startingJobNames.push(action.payload.jobName)
    })
    .addCase(actions.startJobSuccess, (state, action) => {
      state.startingJobNames = state.startingJobNames.filter(
        (name) => name !== action.payload.jobName
      )
    })
    .addCase(actions.startJobFailed, (state, action) => {
      state.startingJobNames = state.startingJobNames.filter(
        (name) => name !== action.payload.jobName
      )
    })
    .addCase(authenticationActions.signOut, () => INITIAL_STATE)
})
